import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, FileText } from 'lucide-react'
import { X } from 'lucide-react'
import {
  useChekiFormTemplates,
  useCreateChekiFormTemplate,
  useUpdateChekiFormTemplate,
  useDeleteChekiFormTemplate,
  type ChekiFormTemplate,
  type FieldDef,
} from '@/hooks/use-cheki-templates'
import FieldBuilder from '@/components/cheki/field-builder'

// ── Template modal ─────────────────────────────────────────────────────────────

interface TemplateModalProps {
  open: boolean
  onClose: () => void
  template?: ChekiFormTemplate
}

function TemplateModal({ open, onClose, template }: TemplateModalProps) {
  const isEdit = !!template
  const create = useCreateChekiFormTemplate()
  const update = useUpdateChekiFormTemplate()

  const [name, setName] = useState(template?.name ?? '')
  const [fields, setFields] = useState<FieldDef[]>(template?.fields ?? [])

  // Sync state when modal opens or template changes
  useEffect(() => {
    if (open) {
      setName(template?.name ?? '')
      setFields(template?.fields ?? [])
    }
  }, [open, template])

  const isPending = create.isPending || update.isPending
  const mutationError = create.error ?? update.error

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = { name: name.trim(), fields }
    if (isEdit && template) {
      update.mutate({ id: template.id, ...payload }, { onSuccess: onClose })
    } else {
      create.mutate(payload, { onSuccess: onClose })
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold">{isEdit ? 'Edit Template' : 'Buat Template'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="space-y-1">
            <label className="text-sm font-medium">Nama Template *</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Contoh: "Template Sesi Reguler"'
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Field</label>
            <FieldBuilder fields={fields} onChange={setFields} />
          </div>

          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan. Coba lagi.'}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Buat Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Field type label map ───────────────────────────────────────────────────────

const TYPE_LABELS: Record<FieldDef['type'], string> = {
  text: 'Teks',
  textarea: 'Area Teks',
  select: 'Pilihan',
  number: 'Angka',
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ChekiTemplatesPage() {
  const { data: templates = [], isLoading } = useChekiFormTemplates()
  const deleteTemplate = useDeleteChekiFormTemplate()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ChekiFormTemplate | undefined>()

  function openCreate() {
    setEditingTemplate(undefined)
    setModalOpen(true)
  }

  function openEdit(t: ChekiFormTemplate) {
    setEditingTemplate(t)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingTemplate(undefined)
  }

  function handleDelete(t: ChekiFormTemplate) {
    if (!confirm(`Hapus template "${t.name}"? Template tidak akan lagi tersedia di form cheki baru.`)) return
    deleteTemplate.mutate(t.id)
  }

  if (isLoading) return <p className="text-muted-foreground">Memuat template...</p>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Template Field</h1>
          <p className="text-muted-foreground">
            Buat set field tambahan yang bisa di-reuse saat membuat form cheki.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Buat Template
        </button>
      </div>

      {/* Empty state */}
      {templates.length === 0 && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <FileText className="mx-auto h-8 w-8 mb-3 opacity-40" />
          <p className="text-sm">
            Belum ada template. Buat template untuk mempercepat setup form cheki.
          </p>
        </div>
      )}

      {/* Cards grid */}
      {templates.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <div
              key={t.id}
              className="rounded-xl border border-border bg-card flex flex-col"
            >
              <div className="p-5 flex-1 space-y-3">
                <p className="font-semibold text-sm">{t.name}</p>

                {t.fields.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Tidak ada field.</p>
                ) : (
                  <ul className="space-y-1">
                    {t.fields.map((f) => (
                      <li key={f.key} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="inline-flex rounded bg-muted px-1.5 py-0.5 font-medium">
                          {TYPE_LABELS[f.type]}
                        </span>
                        <span className="truncate">{f.label}</span>
                        {f.required && (
                          <span className="ml-auto shrink-0 text-destructive">*</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-end border-t border-border px-5 py-3 gap-1">
                <button
                  onClick={() => openEdit(t)}
                  className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  title="Edit template"
                >
                  <Edit className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(t)}
                  className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  title="Hapus template"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateModal open={modalOpen} onClose={closeModal} template={editingTemplate} />
    </div>
  )
}
